import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useParcelIssues } from '../../hooks/useParcelIssues'
import { useAuth } from '../../contexts/AuthContext'
import type { ParcelIssue } from '../../hooks/useParcelIssues'
import { fetchAllRows } from '../../lib/fetchAllRows'

const STATUS_COLORS: Record<string, string> = {
  submitted: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  investigating: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  awaiting_carrier: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  closed: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
}

const TYPE_COLORS: Record<string, string> = {
  lost: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  damaged: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  delayed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  missing_scan: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  wrong_address: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
}

const ISSUE_TYPES = ['lost', 'damaged', 'delayed', 'missing_scan', 'wrong_address']
const STATUSES = ['submitted', 'investigating', 'awaiting_carrier', 'resolved', 'closed']

function fmtDate(d: string | null) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function fmtLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

interface Agent { id: string; name: string }

interface NewIssueForm {
  agent_id: string
  importer_name: string
  cyuk_job_ref: string
  waybill_number: string
  tracking_number: string
  carrier: string
  origin_country: string
  destination_country: string
  ship_date: string
  issue_date: string
  issue_type: string
  cartons: string
  weight_kg: string
  declared_value: string
  currency: string
  notes: string
}

const BLANK: NewIssueForm = {
  agent_id: '',
  importer_name: '',
  cyuk_job_ref: '',
  waybill_number: '',
  tracking_number: '',
  carrier: '',
  origin_country: '',
  destination_country: '',
  ship_date: '',
  issue_date: new Date().toISOString().split('T')[0],
  issue_type: '',
  cartons: '',
  weight_kg: '',
  declared_value: '',
  currency: 'GBP',
  notes: '',
}

export default function ParcelIssues() {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [agentFilter, setAgentFilter] = useState('all')

  const [agents, setAgents] = useState<Agent[]>([])
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<NewIssueForm>(BLANK)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const { issues, stats, loading, error, createIssue } = useParcelIssues({
    searchQuery: debouncedSearch,
    statusFilter,
    typeFilter,
    agentFilter,
  })

  const loadAgents = useCallback(async () => {
    const data = await fetchAllRows<Agent>((from, to) =>
      supabase.from('customers').select('id, name').order('name').order('id').range(from, to) as never)
    setAgents(data)
  }, [])

  useEffect(() => { loadAgents() }, [loadAgents])

  function clearFilters() {
    setSearch('')
    setDebouncedSearch('')
    setStatusFilter('all')
    setTypeFilter('all')
    setAgentFilter('all')
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSaveError(null)
    try {
      const payload: Partial<ParcelIssue> = {
        agent_id: form.agent_id || null,
        importer_name: form.importer_name || null,
        cyuk_job_ref: form.cyuk_job_ref || null,
        waybill_number: form.waybill_number || null,
        tracking_number: form.tracking_number || null,
        carrier: form.carrier || null,
        origin_country: form.origin_country || null,
        destination_country: form.destination_country || null,
        ship_date: form.ship_date || null,
        issue_date: form.issue_date || null,
        issue_type: (form.issue_type as ParcelIssue['issue_type']) || null,
        cartons: form.cartons ? parseInt(form.cartons) : null,
        weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
        declared_value: form.declared_value ? parseFloat(form.declared_value) : null,
        currency: form.currency || 'GBP',
        notes: form.notes || null,
        submitted_by: user?.id ?? null,
        status: 'submitted',
      }
      const created = await createIssue(payload)
      setShowModal(false)
      setForm(BLANK)
      navigate(`/parcel-issues/${created.id}`)
    } catch (e: any) {
      setSaveError(e.message || 'Failed to create issue')
    } finally {
      setSaving(false)
    }
  }

  function set(field: keyof NewIssueForm) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setForm(f => ({ ...f, [field]: e.target.value }))
    }
  }

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white'
  const labelCls = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Parcel Claims</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Track lost, damaged and delayed parcel claims
            </p>
          </div>
          <button
            onClick={() => { setShowModal(true); setSaveError(null); setForm(BLANK) }}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Issue
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            {[
              { label: 'Total', value: stats.total, color: 'text-gray-900 dark:text-white' },
              { label: 'Submitted', value: stats.submitted, color: 'text-blue-600 dark:text-blue-400' },
              { label: 'Investigating', value: stats.investigating, color: 'text-amber-600 dark:text-amber-400' },
              { label: 'Awaiting Carrier', value: stats.awaiting_carrier, color: 'text-orange-600 dark:text-orange-400' },
              { label: 'Resolved', value: stats.resolved, color: 'text-green-600 dark:text-green-400' },
              { label: 'Closed', value: stats.closed, color: 'text-gray-500 dark:text-gray-400' },
            ].map(s => (
              <div key={s.label} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search waybill, issue no, importer…"
              className="md:col-span-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
            <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)} className={inputCls}>
              <option value="all">All Agents</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={inputCls}>
              <option value="all">All Statuses</option>
              {STATUSES.map(s => <option key={s} value={s}>{fmtLabel(s)}</option>)}
            </select>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={inputCls}>
              <option value="all">All Types</option>
              {ISSUE_TYPES.map(t => <option key={t} value={t}>{fmtLabel(t)}</option>)}
            </select>
          </div>
          {(search || statusFilter !== 'all' || typeFilter !== 'all' || agentFilter !== 'all') && (
            <button onClick={clearFilters} className="mt-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline">
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">Loading…</div>
          ) : error ? (
            <div className="p-12 text-center text-red-600 dark:text-red-400">{error}</div>
          ) : issues.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
              No parcel issues found. Click <strong>New Issue</strong> to log one.
            </div>
          ) : (
            <>
              <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                {issues.length} issue{issues.length !== 1 ? 's' : ''}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                    <tr>
                      <th className="px-4 py-3 text-left">Issue No</th>
                      <th className="px-4 py-3 text-left">Agent</th>
                      <th className="px-4 py-3 text-left">Waybill</th>
                      <th className="px-4 py-3 text-left">Importer</th>
                      <th className="px-4 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Issue Date</th>
                      <th className="px-4 py-3 text-left">Job Ref</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {issues.map(issue => (
                      <tr
                        key={issue.id}
                        onClick={() => navigate(`/parcel-issues/${issue.id}`)}
                        className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer"
                      >
                        <td className="px-4 py-3 font-mono font-semibold text-red-700 dark:text-red-400 whitespace-nowrap">
                          {issue.issue_no}
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white whitespace-nowrap">
                          {issue.agent_name || '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {issue.waybill_number || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {issue.importer_name || '—'}
                        </td>
                        <td className="px-4 py-3">
                          {issue.issue_type ? (
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${TYPE_COLORS[issue.issue_type] || ''}`}>
                              {fmtLabel(issue.issue_type)}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_COLORS[issue.status] || ''}`}>
                            {fmtLabel(issue.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                          {fmtDate(issue.issue_date)}
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 font-mono text-xs">
                          {issue.cyuk_job_ref || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Issue Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Log New Parcel Issue</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-5">
              {saveError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">
                  {saveError}
                </div>
              )}

              {/* Row 1 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Agent / Customer</label>
                  <select value={form.agent_id} onChange={set('agent_id')} className={inputCls}>
                    <option value="">— Select agent —</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Importer Name</label>
                  <input type="text" value={form.importer_name} onChange={set('importer_name')} className={inputCls} placeholder="Importer company name" />
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Waybill Number</label>
                  <input type="text" value={form.waybill_number} onChange={set('waybill_number')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Tracking Number</label>
                  <input type="text" value={form.tracking_number} onChange={set('tracking_number')} className={inputCls} />
                </div>
              </div>

              {/* Row 3 */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>CYUK Job Ref</label>
                  <input type="text" value={form.cyuk_job_ref} onChange={set('cyuk_job_ref')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Issue Date</label>
                  <input type="date" value={form.issue_date} onChange={set('issue_date')} required className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Issue Type <span className="text-red-500">*</span></label>
                  <select value={form.issue_type} onChange={set('issue_type')} required className={inputCls}>
                    <option value="">— Select —</option>
                    {ISSUE_TYPES.map(t => <option key={t} value={t}>{fmtLabel(t)}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 4 */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Carrier</label>
                  <input type="text" value={form.carrier} onChange={set('carrier')} className={inputCls} placeholder="e.g. DHL, FedEx" />
                </div>
                <div>
                  <label className={labelCls}>Origin Country</label>
                  <input type="text" value={form.origin_country} onChange={set('origin_country')} className={inputCls} placeholder="CN" />
                </div>
                <div>
                  <label className={labelCls}>Destination Country</label>
                  <input type="text" value={form.destination_country} onChange={set('destination_country')} className={inputCls} placeholder="GB" />
                </div>
              </div>

              {/* Row 5 */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className={labelCls}>Cartons</label>
                  <input type="number" min="0" value={form.cartons} onChange={set('cartons')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Weight (kg)</label>
                  <input type="number" min="0" step="0.001" value={form.weight_kg} onChange={set('weight_kg')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Declared Value</label>
                  <input type="number" min="0" step="0.01" value={form.declared_value} onChange={set('declared_value')} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Currency</label>
                  <select value={form.currency} onChange={set('currency')} className={inputCls}>
                    <option>GBP</option>
                    <option>USD</option>
                    <option>EUR</option>
                    <option>CNY</option>
                  </select>
                </div>
              </div>

              {/* Ship date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Ship Date</label>
                  <input type="date" value={form.ship_date} onChange={set('ship_date')} className={inputCls} />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className={labelCls}>Initial Notes</label>
                <textarea value={form.notes} onChange={set('notes')} rows={3} className={inputCls} placeholder="Describe the issue…" />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
                >
                  {saving ? 'Creating…' : 'Create Issue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
